import { createContext, ReactNode, useCallback, useMemo, useState } from 'react';
import { defaultCoordinates } from '~/components/map/constants';
import { LoanOfficerCluster } from '~/sections/loan-officers/search-results/map-view/loan-officers-sidebar/loan-officer-card/types';

type FilterOption = { name: string; count: number };

type MapContextProps = {
  coordinates: Coordinates;
  types: string[];
  sidebarReopen: boolean;
  sidebarLoanOfficers: LoanOfficerCluster[];
  sidebarCompanies: FilterOption[];
  sidebarStates: FilterOption[];
  sidebarTotalCount: number;
  zoomLevel: number | null;
  mapActions: {
    changeCoordinates: (params: Coordinates, zoomLevel?: number) => void;
    changeTypes: (params: string[]) => void;
    changeSidbarReopen: (params: boolean) => void;
    changeSidebarLoanOfficers: (params: LoanOfficerCluster[]) => void;
    changeSidebarCompanies: (params: FilterOption[]) => void;
    changeSidebarStates: (params: FilterOption[]) => void;
    changeSidebarTotalCount: (params: number) => void;
    changeZoomLevel: (zoomLevel: number | null) => void;
  };
};

const defaultTypes: string[] = [];
const defaultZoomLevel = null;

// Should be renamed to LO Map Context
const initialState: MapContextProps = {
  coordinates: defaultCoordinates,
  types: defaultTypes,
  sidebarReopen: false,
  sidebarLoanOfficers: [],
  sidebarCompanies: [],
  sidebarStates: [],
  sidebarTotalCount: 0,
  zoomLevel: defaultZoomLevel,
  mapActions: {
    changeCoordinates: () => {},
    changeTypes: () => {},
    changeSidbarReopen: () => {},
    changeSidebarLoanOfficers: () => {},
    changeSidebarCompanies: () => {},
    changeSidebarStates: () => {},
    changeSidebarTotalCount: () => {},
    changeZoomLevel: () => {},
  },
};

const LoanOfficersMapContext = createContext(initialState);

type MapProviderProps = {
  children: ReactNode;
};

function LoanOfficersMapProvider({ children }: MapProviderProps) {
  const [coordinates, setCoordinates] = useState(defaultCoordinates);
  const changeCoordinates = useCallback((coordinates: Coordinates, zoomLevel?: number) => {
    if (zoomLevel) {
      setZoomLevel(zoomLevel);
    }
    setCoordinates(coordinates);
  }, []);

  const [types, setTypes] = useState(defaultTypes);
  const changeTypes = useCallback((types: string[]) => {
    setTypes(types);
  }, []);

  const [sidebarReopen, setSidebarReopen] = useState(false);
  const changeSidbarReopen = useCallback((sidebarReopen: boolean) => {
    setSidebarReopen(sidebarReopen);
  }, []);

  const [sidebarLoanOfficers, setSidebarLoanOfficers] = useState<LoanOfficerCluster[]>([]);
  const changeSidebarLoanOfficers = useCallback((sidebarLoanOfficers: LoanOfficerCluster[]) => {
    setSidebarLoanOfficers(sidebarLoanOfficers);
  }, []);

  const [sidebarCompanies, setSidebarCompanies] = useState<FilterOption[]>([]);
  const changeSidebarCompanies = useCallback((sidebarCompanies: FilterOption[]) => {
    setSidebarCompanies(sidebarCompanies);
  }, []);

  const [sidebarStates, setSidebarStates] = useState<FilterOption[]>([]);
  const changeSidebarStates = useCallback((sidebarStates: FilterOption[]) => {
    setSidebarStates(sidebarStates);
  }, []);

  const [sidebarTotalCount, setSidebarTotalCount] = useState(0);
  const changeSidebarTotalCount = useCallback((sidebarTotalCount: number) => {
    setSidebarTotalCount(sidebarTotalCount);
  }, []);
  const [zoomLevel, setZoomLevel] = useState<number | null>(defaultZoomLevel);
  const changeZoomLevel = (newZoomLevel: number | null) => {
    setZoomLevel(newZoomLevel);
  };

  const mapActions = useMemo(
    () => ({
      changeTypes,
      changeCoordinates,
      changeSidbarReopen,
      changeSidebarLoanOfficers,
      changeSidebarCompanies,
      changeSidebarStates,
      changeSidebarTotalCount,
      changeZoomLevel,
    }),
    [
      changeTypes,
      changeCoordinates,
      changeSidbarReopen,
      changeSidebarLoanOfficers,
      changeSidebarCompanies,
      changeSidebarStates,
      changeSidebarTotalCount,
    ]
  );

  return (
    <LoanOfficersMapContext.Provider
      value={{
        coordinates,
        types,
        sidebarReopen,
        sidebarLoanOfficers,
        sidebarCompanies,
        sidebarStates,
        sidebarTotalCount,
        zoomLevel,
        mapActions,
      }}
    >
      {children}
    </LoanOfficersMapContext.Provider>
  );
}

export { LoanOfficersMapProvider, LoanOfficersMapContext };
